import React, { useMemo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { sortReleasesByVersionDesc } from './compareSemver';

function parseReleaseDate(iso) {
  if (!iso) return null;
  const d = parseISO(iso);
  return isValid(d) ? d : null;
}

function formatReleaseDate(iso) {
  const d = parseReleaseDate(iso);
  if (!d) return '';
  return format(d, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
}

export default function DesktopReleaseTimeline({ releases = [], latestVersion, translate }) {
  const sorted = useMemo(() => sortReleasesByVersionDesc(releases), [releases]);

  if (sorted.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        {translate('extensions.eagle_desktop.no_releases')}
      </Typography>
    );
  }

  return (
    <Box
      sx={(theme) => ({
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 6,
          top: 4,
          bottom: 4,
          width: 2,
          bgcolor: theme.palette.divider,
          borderRadius: 1,
        },
      })}
    >
      {sorted.map((release) => (
        <Box key={release.version} sx={{ mb: 3, pl: 3, position: 'relative' }}>
          <Box
            sx={(theme) => ({
              position: 'absolute',
              left: 0,
              top: 6,
              width: 14,
              height: 14,
              borderRadius: '50%',
              bgcolor: theme.palette.primary.main,
              border: `2px solid ${theme.palette.background.paper}`,
            })}
          />
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: 'action.hover',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                v{release.version}
              </Typography>
              {release.version === latestVersion && (
                <Chip size="small" color="primary" label={translate('extensions.eagle_desktop.latest_badge')} />
              )}
            </Box>
            {release.released_at && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {formatReleaseDate(release.released_at)}
              </Typography>
            )}
            {(release.sections || []).map((section, si) => (
              <Box key={si} sx={{ mb: 1.5 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {section.title}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {(section.items || []).map((item, ii) => (
                    <Typography component="li" variant="body2" color="text.secondary" key={ii} sx={{ mb: 0.25 }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
